<template>
  <CollectionDistrictView
    :districts="districts"
    :loading="loading"
    :error="error"
    :show-modal="showModal"
    :show-delete-modal="showDeleteModal"
    :is-edit-mode="isEditMode"
    :form-data="formData"
    :form-errors="formErrors"
    :delete-target="deleteTarget"
    @show-add-modal="showAddModal"
    @show-edit-modal="showEditModal"
    @confirm-delete="confirmDelete"
    @add-district="handleAddDistrict"
    @edit-district="handleEditDistrict"
    @delete-district="handleDeleteDistrict"
    @close-modal="closeModal"
    @close-delete-modal="closeDeleteModal"
    @back-to-menu="handleBackToMenu"
    @refresh="handleRefresh"
    @table-action="handleTableAction"
    @submit="handleSubmit"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import CollectionDistrictView from '../components/collectionDistrict/CollectionDistrictView.vue'
import {
  getCollectionDistricts,
  createCollectionDistrict,
  updateCollectionDistrict,
  deleteCollectionDistrict
} from '@/services/systemSettings/collectionDistrictService.js'

const emit = defineEmits(['show-success', 'back-to-menu'])

const districts = ref([])
const loading = ref(false)
const error = ref(null)

const showModal = ref(false)
const showDeleteModal = ref(false)
const isEditMode = ref(false)

const formData = ref({
  districtName: ''
})
const formErrors = ref({})
const deleteTarget = ref(null)

const resetForm = () => {
  formData.value = {
    districtName: ''
  }
  formErrors.value = {}
}

const showAddModal = () => {
  isEditMode.value = false
  resetForm()
  showModal.value = true
}

const showEditModal = (district) => {
  isEditMode.value = true
  formData.value = { ...district }
  formErrors.value = {}
  showModal.value = true
}

const confirmDelete = (district) => {
  deleteTarget.value = district
  showDeleteModal.value = true
}

const closeModal = () => {
  resetForm()
  showModal.value = false
}

const closeDeleteModal = () => {
  deleteTarget.value = null
  showDeleteModal.value = false
}

/**
 * テーブルアクションの統合ハンドラー
 */
const handleTableAction = (payload) => {
  const { action, data } = payload
  switch (action) {
    case 'edit':
      showEditModal(data)
      break
    case 'delete':
      confirmDelete(data)
      break
    default:
      console.warn('Unknown table action:', action)
  }
}

/**
 * フォーム送信処理（バリデーションをContainerで実施）
 */
const handleSubmit = () => {
  const errors = {}

  if (!formData.value.districtName?.trim()) {
    errors.districtName = '地区名は必須です'
  }

  const existingDistrict = districts.value.find(district =>
    district.districtName === formData.value.districtName?.trim() &&
    (!isEditMode.value || district.id !== formData.value.id)
  )
  if (existingDistrict) {
    errors.districtName = 'この地区名は既に登録されています'
  }

  if (Object.keys(errors).length > 0) {
    formErrors.value = errors
    return
  }

  const districtData = {
    ...formData.value,
    districtName: formData.value.districtName?.trim()
  }

  if (isEditMode.value) {
    handleEditDistrict(districtData)
  } else {
    handleAddDistrict(districtData)
  }
}

const loadDistricts = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await getCollectionDistricts()

    if (response.success) {
      districts.value = response.data
    } else {
      error.value = response.message || '地区データの取得に失敗しました'
    }
  } catch (err) {
    console.error('Failed to load districts:', err)
    error.value = 'データの取得中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleAddDistrict = async (districtData) => {
  loading.value = true
  error.value = null

  try {
    const response = await createCollectionDistrict(districtData)

    if (response.success) {
      districts.value = response.data
      closeModal()
      emit('show-success', response.message || '地区が正常に追加されました')
    } else {
      error.value = response.message || '地区の追加に失敗しました'
    }
  } catch (err) {
    console.error('Failed to add district:', err)
    error.value = '地区の追加中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleEditDistrict = async (districtData) => {
  loading.value = true
  error.value = null

  try {
    const response = await updateCollectionDistrict(districtData.id, districtData)

    if (response.success) {
      districts.value = response.data
      closeModal()
      emit('show-success', response.message || '地区が正常に更新されました')
    } else {
      error.value = response.message || '地区の更新に失敗しました'
    }
  } catch (err) {
    console.error('Failed to update district:', err)
    error.value = '地区の更新中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleDeleteDistrict = async (district) => {
  loading.value = true
  error.value = null

  try {
    const response = await deleteCollectionDistrict(district.id)

    if (response.success) {
      districts.value = response.data
      closeDeleteModal()
      emit('show-success', response.message || '地区が正常に削除されました')
    } else {
      error.value = response.message || '地区の削除に失敗しました'
    }
  } catch (err) {
    console.error('Failed to delete district:', err)
    error.value = '地区の削除中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleBackToMenu = () => {
  emit('back-to-menu')
}

const handleRefresh = async () => {
  await loadDistricts()
}

onMounted(async () => {
  await loadDistricts()
})
</script>

